import { Suspense } from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { ErrorBoundary } from "react-error-boundary";
import { Card, Stack, Text, Title } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useAuth } from "../../hooks/useAuth";
import { useSelectedAirline } from "../../hooks/useSelectedAirline";
import { PeriodPicker } from "./PeriodPicker";
import { CampaignImpactContent, CampaignImpactLoader } from "./CampaignImpactLoader";
import { CampaignImpactSuspense } from "./CampaignImpactSuspense";
import { CampaignImpactMetricsExplainer } from "./CampaignImpactMetricsExplainer";
import { DEFAULT_CAMPAIGN_IMPACT_PERIOD, getAllPeriodOptions, toPeriodKey } from "../../constants";

const CampaignImpactErrorFallback = () => <CampaignImpactContent kpis={[]} isError />;

export const CampaignImpactSection = () => {
  const i18n = useI18n();
  const { isAirlineUser } = useAuth();
  const { selectedAirline } = useSelectedAirline();
  const { period: periodFromUrl } = useSearch({ from: "/metrics" });
  const navigate = useNavigate();

  const validPeriodKeys = getAllPeriodOptions().map(({ periodType, periodValue }) =>
    toPeriodKey(periodType, periodValue)
  );
  const period =
    periodFromUrl && validPeriodKeys.includes(periodFromUrl) ? periodFromUrl : DEFAULT_CAMPAIGN_IMPACT_PERIOD;

  const setPeriod = (newPeriod: string) => {
    navigate({ to: "/metrics", search: (prev) => ({ ...prev, period: newPeriod }) });
  };

  return (
    <Stack direction="column" gap={6}>
      <Stack direction="column" gap={1}>
        <Title title={i18n.trans(t("flights_airline_portal_airline_view_page_header_one"))} variant="headline_2" />
        <Text variant="body_2">
          {isAirlineUser
            ? i18n.trans(t("flights_airline_portal_airline_view_page_subheader_one"))
            : i18n.trans(t("flights_airline_portal_superuser_perf_report_campaign_subheader"))}
        </Text>
      </Stack>

      <PeriodPicker period={period} onChange={setPeriod} />

      <Card mixin={{ padding: 6 }}>
        <Stack direction="column" gap={4}>
          <ErrorBoundary
            resetKeys={[period, selectedAirline?.code]}
            fallback={<CampaignImpactErrorFallback />}
          >
            <Suspense fallback={<CampaignImpactSuspense />}>
              <CampaignImpactLoader period={period} />
            </Suspense>
          </ErrorBoundary>

          <CampaignImpactMetricsExplainer />
        </Stack>
      </Card>
    </Stack>
  );
};
